import React, { FC, ReactNode, useCallback, useContext, useMemo } from "react";
import { useMutation } from "@apollo/client";
import {
  SUBMIT_PATIENT_FEEDBACK,
  SubmitPatientFeedbackData,
  SubmitPatientFeedbackVariables,
} from "@app/service/patientActions";

export interface PatientActionsController {
  submitPatientFeedback: (
    input: SubmitPatientFeedbackVariables["input"]
  ) => Promise<void>;
}

export const PatientActionsContext =
  React.createContext<PatientActionsController | null>(null);

PatientActionsContext.displayName = "PatientActionsContext";

export const usePatientActions = (): PatientActionsController => {
  const ctx = useContext(PatientActionsContext);
  if (!ctx) {
    throw new Error(
      "usePatientActions must be used within PatientActionsProvider"
    );
  }
  return ctx;
};

interface PatientActionsProviderProps {
  children: ReactNode;
}

const PatientActionsProvider: FC<PatientActionsProviderProps> = ({
  children,
}) => {
  const [submitFeedbackMutation] = useMutation<
    SubmitPatientFeedbackData,
    SubmitPatientFeedbackVariables
  >(SUBMIT_PATIENT_FEEDBACK);

  const submitPatientFeedback = useCallback(
    async (input: SubmitPatientFeedbackVariables["input"]) => {
      await submitFeedbackMutation({ variables: { input } });
    },
    [submitFeedbackMutation]
  );

  const value = useMemo<PatientActionsController>(
    () => ({ submitPatientFeedback }),
    [submitPatientFeedback]
  );

  return (
    <PatientActionsContext.Provider value={value}>
      {children}
    </PatientActionsContext.Provider>
  );
};

export default PatientActionsProvider;
